import React, { type JSX } from 'react';
import { getCurrentUser } from '@features/auth';
import { useSessionStore, isJwtTokenExpired } from '@features/auth/store/useSessionStore';
import { Navigate } from 'react-router-dom';
import { publicRoutes } from '@constants';

const RoleProtectedRoute = ({
  allowedType,
  children,
}: {
  allowedType: string[];
  children: React.ReactNode;
}): JSX.Element => {
  const currentUser = getCurrentUser();
  const authToken = useSessionStore((s) => s.authToken);
  const secondaryAuthToken = useSessionStore((s) => s.secondaryAuthToken);
  const isSessionRefreshing = useSessionStore((s) => s.isSessionRefreshing);

  // Only validate expiration of authToken (edge, 2h, no refresh).
  // secondaryAuthToken (auth, 15min) is refreshable — the interceptor and
  // checkSessionValidity handle its renewal.  Checking its expiration here
  // would cause a premature redirect before the refresh has a chance to run.
  const hasValidTokens =
    authToken != null &&
    secondaryAuthToken != null &&
    !isJwtTokenExpired(authToken);

  const hasAnyAllowedType = allowedType.some((type) =>
    currentUser?.type?.includes(type as typeof currentUser.type[number]),
  );

  const isLoggedIn = currentUser != null && hasValidTokens;

  // While a refresh is in progress, avoid redirecting — let it finish.
  if (!isLoggedIn && isSessionRefreshing) {
    return <></>;
  }

  if (!isLoggedIn) {
    return <Navigate to={publicRoutes.LOGIN} replace />;
  }

  if (!hasAnyAllowedType) {
    return <Navigate to="/404" replace />;
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
